import { Events, EventsGlobal } from '@shared/Events';

interface ICacheItem {
    key: string;
    data: any;
    timestamp: number;
    timeout: number;
}

const defaultTimeoutSec = 30;
let isProbablyFull = false;
let isProjectChanging = false;

Events.on(EventsGlobal.PROJECT_CHANGE_BEFORE, () => {
    isProjectChanging = true;
});

const openDB = async (projectScoped = true): Promise<IDBDatabase> => {
    const dbName = projectScoped ? `kuura_p${kuura.getProjectUuid()}_u${kuura.getUserUuid()}}` : `kuura_u${kuura.getUserUuid()}}`;

    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, 1);

        request.onupgradeneeded = event => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains('cache')) {
                db.createObjectStore('cache', { keyPath: 'key' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const withDB = async (operation: (store: IDBObjectStore) => any, projectScoped = true): Promise<any> => {
    if (isProjectChanging) return;

    try {
        const db = await openDB(projectScoped);
        if (isProjectChanging) {
            db.close();
            return;
        }
        const transaction = db.transaction('cache', 'readwrite');
        const store = transaction.objectStore('cache');
        return operation(store);
    } catch (e) {
        console.warn('[ CacheIDB ] withDB error:', e);
    }
};

clearExpired();

async function get(cacheKey: string): Promise<any | null> {
    if (!isEnabled()) return null;

    return withDB(async store => {
        const request = store.get(cacheKey);
        const item = await new Promise<ICacheItem | undefined>(resolve => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(undefined);
        });

        if (!item || item.timestamp + item.timeout * 1000 < Date.now()) {
            if (item) await store.delete(cacheKey);
            return null;
        }

        return item.data;
    });
}

async function has(key: string): Promise<boolean> {
    return !!(await get(key));
}

async function set(key: string, data: any, timeout: number = defaultTimeoutSec): Promise<boolean> {
    if (!isEnabled()) return false;
    if (isProjectChanging) return false;

    try {
        return withDB(async store => {
            if (isProjectChanging) return false;

            store.put({
                key,
                data,
                timestamp: Date.now(),
                timeout,
            });
            return true;
        });
    } catch (e) {
        console.warn('[ CacheIDB ] set error:', e);
    }
}

async function remove(cacheKey: string): Promise<boolean> {
    if (isProjectChanging) return false;

    return withDB(async store => {
        await store.delete(cacheKey);
        return true;
    });
}

function clearExpired() {
    if (!isEnabled()) return false;
    forEach(key => get(key));
}

function forEach(callback: (key: string) => void) {
    const hits: string[] = []; //can't remove while iterating

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.indexOf('_cache_') === 0) {
            hits.push(key);
        }
    }

    for (const hit of hits) {
        callback(hit);
    }
}

function enable() {
    localStorage.setItem('cache_enabled', 'true');
}

function disable() {
    localStorage.setItem('cache_enabled', 'false');
}

function isEnabled(): boolean {
    return localStorage.getItem('cache_enabled') !== 'false';
}

async function getStorageEstimate(): Promise<{ quota: number; usage: number }> {
    if (!isEnabled() || !navigator.storage || !navigator.storage.estimate) {
        return { quota: 0, usage: 0 };
    }

    try {
        const estimate = await navigator.storage.estimate();
        return {
            quota: estimate.quota ?? 0,
            usage: estimate.usage ?? 0,
        };
    } catch (e) {
        console.warn('[ CacheIDB ] getStorageEstimate error:', e);
        return { quota: 0, usage: 0 };
    }
}

async function clear(): Promise<boolean> {
    if (!isEnabled()) return false;

    return new Promise(resolve => {
        withDB(async store => {
            await store.clear();
            console.log('Cache cleared');
            resolve(true);
        });
    });
}

export const CacheIDB = {
    enable,
    disable,
    isEnabled,
    get,
    set,
    has,
    remove,
    clear,
    getStorageEstimate,
    withDB,
};
